import { Button, HStack, Icon, Menu, MenuButton, MenuItem, MenuList, Stack, useDisclosure } from '@chakra-ui/react';
import { useMemo } from 'react';
import { BiCustomize, BiImport, BiPlus, BiShuffle, BiUpload } from 'react-icons/bi';

import { useDashboardWidgets, useDashboardWidgetState } from '../api/dashboardWidget';
import { PageHeader } from '../widgets/PageHeader';
import { useWidgetWizardControls, WidgetWizard } from '../widgets/WidgetWizard/WidgetWizard';
import { DashboardWidget } from './DashboardWidget/DashboardWidget';
import { SchwabRmdUploadModal } from './SchwabRmdUploadModal';
import { stockWidgets } from './stockWidgets/stockWidgets';
import { WidgetOrderModal } from './WidgetOrderModal';

export const Dashboard = () => {
    const { data: customWidgets } = useDashboardWidgets();
    const { data: widgetState } = useDashboardWidgetState();
    const { createWidget, importWidget, isOpen, onClose, showImport } = useWidgetWizardControls();
    const {
        isOpen: widgetOrderModalOpen,
        onOpen: onWidgetOrderModalOpen,
        onClose: onWidgetOrderModalClose,
    } = useDisclosure();
    const {
        isOpen: schwabRmdUploadOpen,
        onOpen: onSchwabRmdUploadOpen,
        onClose: onSchwabRmdUploadClose,
    } = useDisclosure();

    const entries = useMemo(() => {
        return [
            ...stockWidgets,
            ...(customWidgets?.map((def) => ({
                id: def.id,
                title: def.title,
                widget: () => <DashboardWidget widgetDefinition={def} isCustom />,
            })) || []),
        ];
    }, [customWidgets]);

    const display = useMemo(() => {
        if (!widgetState) {
            return [];
        }

        return [...entries.filter((entry) => !widgetState.hiddenWidgets.includes(entry.id))]
            .sort((a, b) => widgetSortFn(a.id, b.id, widgetState.widgetOrder))
            .map((e) => e.widget)
            .filter(Boolean) as (() => JSX.Element)[];
    }, [entries, widgetState]);

    return (
        <>
            <PageHeader title="Dashboard">
                <HStack spacing={2}>
                    <Button leftIcon={<BiUpload />} size="sm" variant="outline" onClick={onSchwabRmdUploadOpen}>
                        Upload Schwab RMD export
                    </Button>
                    <Menu>
                        <MenuButton as={Button} leftIcon={<BiCustomize />} size="sm" variant="outline">
                            Manage widgets
                        </MenuButton>
                        <MenuList zIndex={10}>
                            <MenuItem icon={<Icon boxSize={4} as={BiPlus} />} onClick={createWidget}>
                                Create widget
                            </MenuItem>
                            <MenuItem icon={<Icon boxSize={4} as={BiImport} />} onClick={importWidget}>
                                Import widget
                            </MenuItem>
                            <MenuItem icon={<Icon boxSize={4} as={BiShuffle} />} onClick={onWidgetOrderModalOpen}>
                                Reorder widgets
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            </PageHeader>
            <Stack>
                {display.map((Item, idx) => (
                    <Item key={idx} />
                ))}
            </Stack>

            {isOpen && <WidgetWizard onClose={onClose} showImport={showImport} />}
            {widgetOrderModalOpen && <WidgetOrderModal onClose={onWidgetOrderModalClose} allWidgets={entries} />}
            {schwabRmdUploadOpen && <SchwabRmdUploadModal onClose={onSchwabRmdUploadClose} />}
        </>
    );
};

export const widgetSortFn = (widgetA: string, widgetB: string, order: string[]) => {
    const idxA = order.indexOf(widgetA);
    const idxB = order.indexOf(widgetB);

    if (idxA === -1) {
        return 1;
    }

    if (idxB === -1) {
        return -1;
    }

    return idxA - idxB;
};
